import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    PieLabelRenderProps,
} from "recharts";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../component/ui/Card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../../component/ui/Chart";
import {
    CHART_TYPE_MAP,
    PieChartConfigType,
} from "../constants/chartConstants";

export function QueueInsightsPieChart(chartConfig: PieChartConfigType) {
    const data = chartConfig.data;
    return (
        <Card className="text-neutral-600 dark:text-[#CEE0EF] dark:bg-[#2D3748] bg-white w-full h-full shadow-none border-none min-h-[260px]">
            <CardHeader className="p-0 space-y-0 pt-[10px]">
                <CardTitle className="text-center text-lg font-semibold text-neutral-600 dark:text-[#CEE0EF]">
                    {chartConfig.title}
                </CardTitle>
            </CardHeader>
            <>
                {data.length > 0 ? (
                    <CardContent className="p-0 flex flex-col items-center h-[calc(100%-40px)]">
                        <div className="w-full h-[calc(100%-45px)]">
                            <ResponsiveContainer width="100%" height="100%">
                                <ChartContainer config={CHART_TYPE_MAP}>
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            cx="50%"
                                            cy="50%"
                                            className="outline-none border-none"
                                            innerRadius="45%"
                                            outerRadius="70%"
                                            dataKey="value"
                                            nameKey="type"
                                            stroke="none"
                                            labelLine={({
                                                cx,
                                                cy,
                                                midAngle,
                                                innerRadius,
                                                outerRadius,
                                                percent,
                                                index,
                                            }: PieLabelRenderProps) => {
                                                if (
                                                    !cx ||
                                                    !cy ||
                                                    !outerRadius ||
                                                    percent === undefined ||
                                                    typeof index !== "number"
                                                )
                                                    return <></>;
                                                const RADIAN = Math.PI / 180;
                                                const radius =
                                                    Number(outerRadius) + 15;
                                                const x =
                                                    Number(cx) +
                                                    radius *
                                                        Math.cos(
                                                            -midAngle * RADIAN
                                                        );
                                                const y =
                                                    Number(cy) +
                                                    radius *
                                                        Math.sin(
                                                            -midAngle * RADIAN
                                                        );
                                                const segmentColor =
                                                    CHART_TYPE_MAP[
                                                        data[index].type
                                                    ].color;
                                                return (
                                                    <line
                                                        x1={
                                                            Number(cx) +
                                                            Number(
                                                                outerRadius
                                                            ) *
                                                                Math.cos(
                                                                    -midAngle *
                                                                        RADIAN
                                                                )
                                                        }
                                                        y1={
                                                            Number(cy) +
                                                            Number(
                                                                outerRadius
                                                            ) *
                                                                Math.sin(
                                                                    -midAngle *
                                                                        RADIAN
                                                                )
                                                        }
                                                        x2={x}
                                                        y2={y}
                                                        stroke={segmentColor}
                                                        strokeWidth={1.5}
                                                        opacity={
                                                            percent >= 0.02
                                                                ? 1
                                                                : 0
                                                        }
                                                    />
                                                );
                                            }}
                                            label={({
                                                cx,
                                                cy,
                                                midAngle,
                                                innerRadius,
                                                outerRadius,
                                                percent,
                                                index,
                                            }) => {
                                                // Calculate position for label
                                                const RADIAN = Math.PI / 180;
                                                const radius = outerRadius + 25;
                                                const x =
                                                    cx +
                                                    radius *
                                                        Math.cos(
                                                            -midAngle * RADIAN
                                                        );
                                                const y =
                                                    cy +
                                                    radius *
                                                        Math.sin(
                                                            -midAngle * RADIAN
                                                        );
                                                return (
                                                    <>
                                                        {percent >= 0.02 && (
                                                            <text
                                                                x={
                                                                    midAngle >
                                                                        150 &&
                                                                    midAngle <
                                                                        210
                                                                        ? x + 6
                                                                        : midAngle >
                                                                                90 &&
                                                                            midAngle <
                                                                                270
                                                                          ? x
                                                                          : midAngle <
                                                                                  60 ||
                                                                              midAngle >
                                                                                  300
                                                                            ? x -
                                                                              3
                                                                            : x +
                                                                              1
                                                                }
                                                                y={
                                                                    midAngle <
                                                                    180
                                                                        ? y + 8
                                                                        : y - 8
                                                                }
                                                                textAnchor={
                                                                    x > cx
                                                                        ? "start"
                                                                        : "end"
                                                                }
                                                                dominantBaseline="central"
                                                                className="text-sm fill-neutral-600 dark:fill-[#CEE0EF]"
                                                            >
                                                                {
                                                                    data[index]
                                                                        .value
                                                                }
                                                            </text>
                                                        )}
                                                    </>
                                                );
                                            }}
                                        >
                                            {data.map((entry, idx) => (
                                                <Cell
                                                    key={`cell-${idx}`}
                                                    fill={
                                                        CHART_TYPE_MAP[
                                                            entry.type
                                                        ].color
                                                    }
                                                />
                                            ))}
                                        </Pie>
                                        <ChartTooltip
                                            cursor={false}
                                            content={<ChartTooltipContent />}
                                        />
                                    </PieChart>
                                </ChartContainer>
                            </ResponsiveContainer>
                        </div>
                        <div className="h-[45px] w-full flex items-center justify-center">
                            <div className="text-[13px] text-center">
                                Total Nameplate Capacity:<br></br>
                                {chartConfig.summary ? chartConfig.summary + "GW" : "N/A"}
                            </div>
                        </div>
                    </CardContent>
                ) : (
                    <CardContent className="flex items-center justify-center h-[calc(100%-40px)]">
                        No data available
                    </CardContent>
                )}
            </>
        </Card>
    );
}

export default QueueInsightsPieChart;
